import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { GameCard } from '../components/GameCard';
import { AddGameModal } from '../components/AddGameModal';
import { Helmet } from 'react-helmet-async';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useRegion } from '../contexts/RegionContext';

interface Game {
  id: string;
  slug: string;
  title: string;
  description: string;
  image_icon: string;
  download_url?: string;
  download_title?: string;
  status: 'in_progress' | 'released' | 'coming_soon';
  is_unreleased?: boolean;
  is_visible: boolean;
  content_blocks: Array<{
    type: 'image' | 'video' | 'text';
    src?: string;
    alt?: string;
    caption?: string;
    content?: string;
  }>;
}

export { default as Contact } from './Contact';

export const Games: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isAdmin = useIsAdmin();
  const { t, direction } = useRegion();

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const { data, error } = await supabase
          .from('games')
          .select('*')
          .eq('is_visible', true);

        if (error) throw error;
        setGames(data || []);
      } catch (error) {
        console.error('Error fetching games:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGames();
  }, []);

  const handleAddGame = async (newGame: Omit<Game, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .insert([newGame])
        .select()
        .single();

      if (error) {
        // If download_title or is_unreleased column does not exist yet
        if (error.code === '42703' || error.message?.includes('column')) {
          const { download_title, is_unreleased, ...safePayload } = newGame;
          const retryRes = await supabase
            .from('games')
            .insert([safePayload])
            .select()
            .single();

          if (retryRes.error) throw retryRes.error;
          setGames([...games, { ...retryRes.data, download_title: newGame.download_title }]);
          return;
        }
        throw error;
      }
      setGames([...games, data]);
    } catch (error) {
      console.error('Error adding game:', error);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl animate-pulse">{t('games.loading')}</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('games.metaTitle')}</title>
        <meta name="description" content={t('games.metaDescription')} />
        <meta name="keywords" content="بازی, گیم, بازی ایرانی, بازی کامپیوتر, دانلود بازی, Active Legend" />
        <link rel="canonical" href="https://activelegend.ir/games" />
        <meta property="og:title" content={t('games.metaTitle')} />
        <meta property="og:description" content={t('games.metaDescription')} />
        <meta property="og:url" content="https://activelegend.ir/games" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-black pt-28 pb-16" dir={direction}>
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl md:text-4xl font-black text-white">{t('games.title')}</h1>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {games.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>

          {isAdmin && (
            <>
              <button
                onClick={() => setShowAddModal(true)}
                className={`fixed bottom-8 ${
                  direction === 'rtl' ? 'left-8' : 'right-8'
                } bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-full shadow-2xl hover:scale-105 transition-all z-40`}
              >
                + افزودن بازی جدید
              </button>

              <AddGameModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSave={handleAddGame}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Games;
